import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import {
    addDriverBonusAdjustment,
    evaluateDriverBonus,
    getDriverBonusDashboard,
    overrideDriverBonusEvent,
    reviewDriverBonusEvent,
    saveDriverBonusConfig,
} from '../api/driverBonus';
import { useAlert } from '../providers/AlertProvider';
import { ActivityIndicator } from './custom/ActivityIndicator';


const DEFAULT_CONFIG = {
    enabled: false,
    start_points: 3,
    bonus_amount: 0,
    frequency_months: 3,
    anchor_date: '2026-10-01',
    missed_pod_points: 1,
    missed_check_points: 1,
    infringement_points: 1,
    grace_days: 1,
    eligible_driver_ids: [],
};

const EVENT_LABELS = {
    missing_pod: 'Missing POD',
    missing_vehicle_check: 'Missing vehicle check',
    tacho_infringement: 'Tacho infringement',
    accident_disqualification: 'Accident disqualification',
    manual_adjustment: 'Manual adjustment',
    override: 'Management override',
};


const DriverBonusPanel = ({ theme }) => {
    const { showAlert } = useAlert();
    const [dashboard, setDashboard] = useState(null);
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [manual, setManual] = useState({ profile_id: '', points_delta: -1, reason: '' });
    const showAlertRef = useRef(showAlert);
    const dark = theme === 'dark';
    const border = dark ? '#555' : '#d8dde3';
    const muted = dark ? '#bbb' : '#667085';

    useEffect(() => {
        showAlertRef.current = showAlert;
    }, [showAlert]);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getDriverBonusDashboard();
            setDashboard(data);
            setConfig({ ...DEFAULT_CONFIG, ...(data.config || {}) });
        } catch (error) {
            showAlertRef.current({ message: error.message, type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const updateConfigField = (field, value) => {
        setConfig((current) => ({ ...current, [field]: value }));
    };

    const toggleDriver = (profileId) => {
        setConfig((current) => ({
            ...current,
            eligible_driver_ids: current.eligible_driver_ids.includes(profileId)
                ? current.eligible_driver_ids.filter((id) => id !== profileId)
                : [...current.eligible_driver_ids, profileId],
        }));
    };

    const saveConfig = async () => {
        setSaving(true);
        try {
            await saveDriverBonusConfig(config);
            showAlert({ message: 'Driver bonus settings saved', type: 'success', buttons: [{ label: 'Close' }] });
            await loadDashboard();
        } catch (error) {
            showAlert({ message: error.message, type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setSaving(false);
        }
    };

    const runEvaluation = async () => {
        setSaving(true);
        try {
            const result = await evaluateDriverBonus();
            showAlert({ message: `${result.message}. ${result.created || 0} event(s) created.`, type: 'success', buttons: [{ label: 'Close' }] });
            await loadDashboard();
        } catch (error) {
            showAlert({ message: error.message, type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            setSaving(false);
        }
    };

    const reviewEvent = async (eventId, decision) => {
        const reason = window.prompt(`Reason to ${decision} this deduction:`);
        if (!reason) return;
        try {
            await reviewDriverBonusEvent(eventId, decision, reason);
            await loadDashboard();
        } catch (error) {
            showAlert({ message: error.message, type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const overrideEvent = async (eventId) => {
        const reason = window.prompt('Management override reason:');
        if (!reason) return;
        try {
            await overrideDriverBonusEvent(eventId, reason);
            await loadDashboard();
        } catch (error) {
            showAlert({ message: error.message, type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const addAdjustment = async (event) => {
        event.preventDefault();
        if (!dashboard?.period) return;
        try {
            await addDriverBonusAdjustment({ ...manual, period_id: dashboard.period.id });
            setManual({ profile_id: '', points_delta: -1, reason: '' });
            await loadDashboard();
        } catch (error) {
            showAlert({ message: error.message, type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    if (loading && !dashboard) return <ActivityIndicator />;

    const drivers = dashboard?.drivers || [];
    const events = dashboard?.events || [];
    const pendingEvents = events.filter((event) => event.status === 'pending');
    const reversedIds = new Set(events.map((event) => event.reverses_event_id).filter(Boolean));
    const activeDecisions = events.filter((event) => (
        event.status === 'applied'
        && event.event_type !== 'override'
        && !reversedIds.has(event.id)
        && (event.points_delta < 0 || event.disqualifies)
    ));
    const inputStyle = { backgroundColor: dark ? '#222' : '#fff', color: dark ? '#fff' : '#111', borderColor: border };
    const sectionStyle = { padding: '22px 0', borderTop: `1px solid ${border}` };
    const tableStyle = {
        minWidth: 760,
        '--bs-table-bg': dark ? '#181818' : '#fff',
        '--bs-table-color': dark ? '#f3f4f6' : '#212529',
        '--bs-table-border-color': dark ? '#444' : '#dee2e6',
        '--bs-table-hover-bg': dark ? '#242424' : '#f5f7fa',
        '--bs-table-hover-color': dark ? '#fff' : '#212529',
    };

    return (
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 20, marginBottom: 22, flexWrap: 'wrap' }}>
                <div>
                    <h3 style={{ marginBottom: 4 }}>Driver Bonus</h3>
                    <div style={{ color: muted }}>Private bonus configuration, decisions and audit history</div>
                </div>
                <Button variant="outline-primary" onClick={runEvaluation} disabled={saving || !config.enabled}>Run checks now</Button>
            </div>

            <section style={sectionStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'center', marginBottom: 18 }}>
                    <div>
                        <h5 style={{ margin: 0 }}>Scheme settings</h5>
                        <small style={{ color: muted }}>Changes apply to newly generated periods.</small>
                    </div>
                    <Form.Check
                        type="switch"
                        id="driver-bonus-enabled"
                        label={config.enabled ? 'Enabled' : 'Disabled'}
                        checked={config.enabled}
                        onChange={(event) => updateConfigField('enabled', event.target.checked)}
                    />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                    <Form.Group><Form.Label>Starting points</Form.Label><Form.Control style={inputStyle} type="number" min="1" value={config.start_points} onChange={(e) => updateConfigField('start_points', Number(e.target.value))} /></Form.Group>
                    <Form.Group><Form.Label>Bonus amount (GBP)</Form.Label><Form.Control style={inputStyle} type="number" min="0" step="0.01" value={config.bonus_amount} onChange={(e) => updateConfigField('bonus_amount', Number(e.target.value))} /></Form.Group>
                    <Form.Group><Form.Label>Frequency</Form.Label><Form.Select style={inputStyle} value={config.frequency_months} onChange={(e) => updateConfigField('frequency_months', Number(e.target.value))}><option value="1">Monthly</option><option value="3">Quarterly</option><option value="6">Six monthly</option><option value="12">Annual</option></Form.Select></Form.Group>
                    <Form.Group><Form.Label>First period starts</Form.Label><Form.Control style={inputStyle} type="date" value={config.anchor_date} onChange={(e) => updateConfigField('anchor_date', e.target.value)} /></Form.Group>
                    <Form.Group><Form.Label>Missed POD deduction</Form.Label><Form.Control style={inputStyle} type="number" min="0" value={config.missed_pod_points} onChange={(e) => updateConfigField('missed_pod_points', Number(e.target.value))} /></Form.Group>
                    <Form.Group><Form.Label>Missing check deduction</Form.Label><Form.Control style={inputStyle} type="number" min="0" value={config.missed_check_points} onChange={(e) => updateConfigField('missed_check_points', Number(e.target.value))} /></Form.Group>
                    <Form.Group><Form.Label>Infringement deduction</Form.Label><Form.Control style={inputStyle} type="number" min="0" value={config.infringement_points} onChange={(e) => updateConfigField('infringement_points', Number(e.target.value))} /></Form.Group>
                    <Form.Group><Form.Label>Grace days</Form.Label><Form.Control style={inputStyle} type="number" min="0" max="14" value={config.grace_days} onChange={(e) => updateConfigField('grace_days', Number(e.target.value))} /></Form.Group>
                </div>
            </section>

            <section style={sectionStyle}>
                <h5>Eligible drivers</h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 9, marginTop: 14 }}>
                    {drivers.map((driver) => (
                        <Form.Check key={driver.id} id={`bonus-driver-${driver.id}`} checked={config.eligible_driver_ids.includes(driver.id)} onChange={() => toggleDriver(driver.id)} label={`${driver.name} ${driver.surname}`} />
                    ))}
                </div>
                <Button style={{ marginTop: 20 }} onClick={saveConfig} disabled={saving}>{saving ? 'Saving...' : 'Save scheme'}</Button>
            </section>

            {dashboard?.period && (
                <section style={sectionStyle}>
                    <h5>Current period</h5>
                    <div style={{ color: muted, marginBottom: 14 }}>{dashboard.period.start_date} to {dashboard.period.end_date}</div>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table table-hover" style={tableStyle}>
                            <thead><tr><th>Driver</th><th>Points</th><th>Status</th><th>Why points were lost</th><th>Bonus</th></tr></thead>
                            <tbody>
                                {(dashboard.participants || []).map((participant) => {
                                    const decisions = activeDecisions.filter((event) => event.profile_id === participant.profile_id);
                                    return (
                                        <tr key={participant.id}>
                                            <td>{participant.driver ? `${participant.driver.name} ${participant.driver.surname}` : participant.profile_id}</td>
                                            <td><strong>{participant.points}</strong> / {participant.starting_points}</td>
                                            <td>{participant.disqualified ? 'Disqualified' : participant.qualifies ? 'Qualifying' : 'Bonus lost'}</td>
                                            <td style={{ minWidth: 320 }}>
                                                {decisions.length === 0 ? <span style={{ color: muted }}>No deductions</span> : decisions.map((event) => (
                                                    <div key={event.id} style={{ padding: '7px 0', borderBottom: `1px solid ${border}` }}>
                                                        <div><strong>{event.event_date}</strong> · {EVENT_LABELS[event.event_type] || event.event_type}</div>
                                                        <div style={{ color: muted, margin: '2px 0 6px' }}>{event.reason}</div>
                                                        <Button size="sm" variant="outline-warning" onClick={() => overrideEvent(event.id)}>
                                                            {event.disqualifies ? 'Restore eligibility' : `Give ${Math.abs(event.points_delta)} point${Math.abs(event.points_delta) === 1 ? '' : 's'} back`}
                                                        </Button>
                                                    </div>
                                                ))}
                                            </td>
                                            <td>{participant.qualifies ? `GBP ${Number(config.bonus_amount).toFixed(2)}` : '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {pendingEvents.length > 0 && (
                <section style={sectionStyle}>
                    <h5>Pending POD decisions</h5>
                    {pendingEvents.map((event) => (
                        <div key={event.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: `1px solid ${border}`, flexWrap: 'wrap' }}>
                            <div><strong>{event.event_date}</strong><div>{event.reason}</div></div>
                            <div style={{ display: 'flex', gap: 8 }}><Button size="sm" variant="danger" onClick={() => reviewEvent(event.id, 'approve')}>Apply</Button><Button size="sm" variant="outline-secondary" onClick={() => reviewEvent(event.id, 'reject')}>Dismiss</Button></div>
                        </div>
                    ))}
                </section>
            )}

            {dashboard?.period && (
                <section style={sectionStyle}>
                    <h5>Manual deduction</h5>
                    <Form onSubmit={addAdjustment} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 130px minmax(220px, 2fr) auto', gap: 10, alignItems: 'end', marginTop: 14 }}>
                        <Form.Group><Form.Label>Driver</Form.Label><Form.Select style={inputStyle} required value={manual.profile_id} onChange={(e) => setManual({ ...manual, profile_id: e.target.value })}><option value="">Select driver</option>{(dashboard.participants || []).map((participant) => <option key={participant.profile_id} value={participant.profile_id}>{participant.driver ? `${participant.driver.name} ${participant.driver.surname}` : participant.profile_id}</option>)}</Form.Select></Form.Group>
                        <Form.Group><Form.Label>Points</Form.Label><Form.Control style={inputStyle} required type="number" min="-100" max="-1" value={manual.points_delta} onChange={(e) => setManual({ ...manual, points_delta: Number(e.target.value) })} /></Form.Group>
                        <Form.Group><Form.Label>Reason</Form.Label><Form.Control style={inputStyle} required minLength="3" value={manual.reason} onChange={(e) => setManual({ ...manual, reason: e.target.value })} /></Form.Group>
                        <Button type="submit">Record</Button>
                    </Form>
                </section>
            )}

            <section style={sectionStyle}>
                <h5>Audit history</h5>
                <div style={{ overflowX: 'auto', marginTop: 12 }}>
                    <table className="table table-hover" style={{ ...tableStyle, minWidth: 900 }}>
                        <thead><tr><th>Date</th><th>Driver</th><th>Event</th><th>Points</th><th>Status</th><th>Reason</th><th></th></tr></thead>
                        <tbody>
                            {events.map((event) => {
                                const driver = drivers.find((item) => item.id === event.profile_id);
                                const canOverride = event.status === 'applied' && event.event_type !== 'override' && !reversedIds.has(event.id);
                                const actionLabel = event.disqualifies ? 'Restore eligibility' : `Give ${Math.abs(event.points_delta)} point${Math.abs(event.points_delta) === 1 ? '' : 's'} back`;
                                return <tr key={event.id}><td>{event.event_date}</td><td>{driver ? `${driver.name} ${driver.surname}` : event.profile_id}</td><td>{EVENT_LABELS[event.event_type] || event.event_type}</td><td>{event.points_delta > 0 ? `+${event.points_delta}` : event.points_delta}</td><td>{reversedIds.has(event.id) ? 'overridden' : event.status}</td><td>{event.reason}</td><td>{canOverride && <Button size="sm" variant="outline-warning" onClick={() => overrideEvent(event.id)}>{actionLabel}</Button>}</td></tr>;
                            })}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default DriverBonusPanel;